/* @flow */
import * as React from 'react';
import { css, names } from 'linaria';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { History, Location } from 'react-router';
import queryString from 'query-string';

import { Card, Button, Icon, Text } from './core';
import Colors from '../constants/Colors';
import MediaQueries from '../constants/MediaQueries';
import { ESTIMATE_PAGE_PATH } from '../../Constants';
import type { IconName } from './core/Icon';
import withBreakpoint from './withBreakpoint';
import tripRequests from '../../redux/trip-requests';
import Spinner from './Spinner';
import reduxPartners from '../../redux/partners';
import LocationPicker from './LocationPicker';
import type { Place } from '../../data/TripRequests';

const LabeledInputStyles = {
  Container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
  Icon: css`
    display: flex;
    width: 50px;
    align-items: center;
    justify-content: center;
  `,
  Body: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 14px;
  `,
  BodyWithIcon: css`
    padding: 14px 14px 14px 0;
  `,
  Label: css`
    margin-top: 2px;
    margin-bottom: 3px;
  `,
};

function LabeledInput({
  icon,
  label,
  children,
}: {
  icon?: IconName,
  label: string,
  children?: any,
}) {
  const iconWrapper = icon && (
    <div className={LabeledInputStyles.Icon} key="icon">
      <Icon name={icon} />
    </div>
  );

  const body = (
    <div
      className={names(LabeledInputStyles.Body, icon && LabeledInputStyles.BodyWithIcon)}
      key="body">
      <Text size="14" color={Colors.GrayAlt1} className={LabeledInputStyles.Label}>
        {label}
      </Text>
      {children}
    </div>
  );

  return <div className={LabeledInputStyles.Container}>{icon ? [iconWrapper, body] : body}</div>;
}

type SupportedLayout = 'vertical' | 'horizontal';

class EstimateFormImpl extends React.Component<
  {
    location: Location,
    history: History,
    originId: ?string,
    destinationId: ?string,
    layout: SupportedLayout,
    hiddenButton?: boolean,
    setNewTripRequestOriginId: (originId: string) => Object,
    setNewTripRequestDestinationId: (destinationId: string) => Object,
    setNewTripRequestQueryString: (query: string) => void,
    buttonLabel?: string,
    className?: string,
    breakpoint: BreakpointSize,
    highlightOriginPlaceholder: boolean,
    originError: boolean,
    destinationError: boolean,
    buttonTheme?: string,
    hideInactiveFieldsOnMobile?: boolean,
  },
  {
    loading: boolean,
    originFocused: boolean,
    destinationFocused: boolean,
    originError: boolean,
    destinationError: boolean,
    originPromise: ?Promise<Place>,
    destinationPromise: ?Promise<Place>,
  }
> {
  state = {
    originFocused: false,
    destinationFocused: false,
    originError: false,
    destinationError: false,
    originPromise: null,
    destinationPromise: null,
    loading: false,
  };

  static defaultProps = {
    layout: 'vertical',
    buttonTheme: 'yellow',
  };

  _destinationInput: { focus(): void };

  _onOriginFocus = () => this.setState({ originFocused: true, originError: false });
  _onOriginBlur = () => this.setState({ originFocused: false });
  _onDestinationFocus = () => this.setState({ destinationFocused: true, destinationError: false });
  _onDestinationBlur = () => this.setState({ destinationFocused: false });

  _onOriginChangeAsync = promise => {
    this.setState({ originPromise: promise });
  };
  _onOriginSelect = async location => {
    const { breakpoint } = this.props;
    if (location) {
      this.props.setNewTripRequestOriginId(location.id);
      if (breakpoint === 'small') {
        if (document.activeElement) document.activeElement.blur();
        this.setState({ originFocused: false });
      } else if (this._destinationInput) {
        this._destinationInput.focus();
        this.setState({ originFocused: false, destinationFocused: true });
      }
    } else {
      this.props.setNewTripRequestOriginId(null);
    }
  };

  _onDestinationChangeAsync = promise => {
    this.setState({ destinationPromise: promise });
  };

  _onDestinationSelect = async location => {
    const { breakpoint } = this.props;
    if (location) {
      this.props.setNewTripRequestDestinationId(location.id);
      if (breakpoint === 'small') {
        if (document.activeElement) document.activeElement.blur();
        this.setState({ destinationFocused: false });
      }
    } else {
      this.props.setNewTripRequestDestinationId(null);
    }
  };

  _onSubmit = async event => {
    event.preventDefault();
    const { history, location } = this.props;

    let { originId } = this.props;
    if (this.state.originPromise) {
      this.setState({ loading: true });
      originId = await this.state.originPromise;
      this.setState({ loading: false });
    }
    if (!originId) {
      this.setState({ originError: true });
      return;
    }

    let { destinationId } = this.props;
    if (this.state.destinationPromise) {
      this.setState({ loading: true });
      destinationId = await this.state.destinationPromise;
      this.setState({ loading: false });
    }
    if (!destinationId) {
      this.setState({ destinationError: true });
      return;
    }

    if (location.search) {
      this.props.setNewTripRequestQueryString(location.search);
    }

    if (location.pathname === '/book' || location.pathname === '/book/location') {
      history.push('/book/product');
    } else {
      // We're on another page. Navigate to the estimates page
      history.push({
        pathname: '/estimate',
        search: queryString.stringify({
          ...queryString.parse(location.search),
          origin_id: originId,
          destination_id: destinationId,
        }),
      });
    }

    // TODO
    /*
    Segment.track('Product List Viewed', {
      products: estimates.map(est => ({
        product_id: est.product.slug,
        name: est.product.slug,
        price: est.low_fare.total / 100,
      })),
    });
    Segment.track('Estimate Requested', {
      originText,
      destinationText,
      originGoogleId,
      destinationGoogleId,
    });
    */
  };

  getButtonText() {
    if (this.props.buttonLabel) return this.props.buttonLabel;
    if (this.state.changed) return 'Update';
    return 'Get estimate';
  }

  render() {
    const {
      layout,
      className,
      hiddenButton,
      originId,
      destinationId,
      highlightOriginPlaceholder,
      buttonTheme,
      hideInactiveFieldsOnMobile,
    } = this.props;
    const {
      originFocused,
      destinationFocused,
      originError,
      destinationError,
      loading,
    } = this.state;
    return (
      <form className={className} onSubmit={this._onSubmit}>
        <Card
          elevated={true}
          bordered={true}
          className={names(
            Styles.Card,
            layout === 'vertical' ? Styles.VerticalCard : Styles.HorizontalCard
          )}>
          <label
            htmlFor="origin"
            className={names(
              Styles.InputWrapper,
              hideInactiveFieldsOnMobile && destinationFocused && Styles.HiddenInputWrapper
              // highlightOriginPlaceholder && Styles.HighlightedPlaceholder
            )}>
            <LabeledInput label="Pickup address" icon="arrow-gray-up">
              <LocationPicker
                placeholder={
                  highlightOriginPlaceholder ? 'Tap to enter pickup address' : 'Enter pickup'
                }
                value={originId}
                onChange={this._onOriginSelect}
                onChangeAsync={this._onOriginChangeAsync}
                onFocus={this._onOriginFocus}
                onBlur={this._onOriginBlur}
                showError={originError}
                showLoading={true}
                id="origin"
              />
            </LabeledInput>
          </label>
          <div
            className={names(
              Styles.DividerWrapper,
              layout === 'vertical'
                ? Styles.VerticalDividerWrapper
                : Styles.HorizontalDividerWrapper,
              hideInactiveFieldsOnMobile &&
                (originFocused || destinationFocused) &&
                Styles.HiddenDividerWrapper
            )}>
            <div className={Styles.Divider} />
          </div>
          <label
            htmlFor="destination"
            className={names(
              Styles.InputWrapper,
              hideInactiveFieldsOnMobile && originFocused && Styles.HiddenInputWrapper
            )}>
            <LabeledInput label="Destination" icon="arrow-gray-down">
              <LocationPicker
                placeholder="Enter destination"
                value={destinationId}
                onChange={this._onDestinationSelect}
                onChangeAsync={this._onDestinationChangeAsync}
                ref={c => (this._destinationInput = c && c.getWrappedInstance())}
                onFocus={this._onDestinationFocus}
                onBlur={this._onDestinationBlur}
                showError={destinationError}
                showLoading={true}
                id="destination"
              />
            </LabeledInput>
          </label>
          <div
            className={names(
              Styles.ButtonWrapper,
              layout === 'vertical' ? Styles.VerticalButtonWrapper : Styles.HorizontalButtonWrapper,
              hiddenButton && Styles.HiddenButtonWrapper
            )}>
            <Button
              theme={buttonTheme}
              disabled={loading}
              bordered={false}
              className={Styles.Button}>
              {loading ? <Spinner /> : this.getButtonText()}
            </Button>
          </div>
        </Card>
      </form>
    );
  }
}

const EstimateForm = compose(
  connect(
    state => ({
      originId: tripRequests.selectors.getNewTripRequest(state).originId,
      destinationId: tripRequests.selectors.getNewTripRequest(state).destinationId,
      partnerAddress: reduxPartners.selectors.getAddress(state),
    }),
    dispatch => ({
      setNewTripRequestOriginId: originId =>
        dispatch(tripRequests.actions.setNewTripRequestOriginId(originId)),
      setNewTripRequestDestinationId: destinationId =>
        dispatch(tripRequests.actions.setNewTripRequestDestinationId(destinationId)),
      setNewTripRequestQueryString: (query: string) =>
        dispatch(tripRequests.actions.setNewTripRequestQueryString(query)),
    })
  ),
  withRouter
)(EstimateFormImpl);

export default EstimateForm;

const layouts = {
  small: 'vertical',
  medium: 'vertical',
  large: 'horizontal',
  xlarge: 'horizontal',
};

export const ResponsiveEstimateForm = withBreakpoint(
  ({ breakpoint, ...props }: { breakpoint: BreakpointSize }) => (
    <EstimateForm layout={layouts[breakpoint]} breakpoint={breakpoint} {...props} />
  )
);

const Styles = {
  Card: css`
    overflow: visible; /* for typeahead */
    padding: 0;
    position: relative;
    align-items: stretch;
    border-radius: 0;
    ${MediaQueries.Medium} {
      border-radius: 10px;
    }
  `,
  HorizontalCard: css`
    flex-direction: row;
    align-items: stretch;
  `,
  VerticalCard: css`
    display: block;
  `,
  InputWrapper: css`
    flex: 1;
    position: relative;
    display: block;
  `,
  HiddenInputWrapper: css`
    opacity: 0;
    position: absolute;
    ${MediaQueries.Medium} {
      opacity: 1;
      position: relative;
    }
  `,
  ButtonWrapper: css`
    flex: 1;
  `,
  HorizontalButtonWrapper: css`
    padding: 12px;
    flex-basis: auto;
    flex-grow: 1;
    max-width: 190px;
  `,
  VerticalButtonWrapper: css`
    padding: 0 12px 12px 12px;
  `,
  HiddenButtonWrapper: css`
    visibility: hidden;
    position: absolute;
  `,
  Button: css`
    display: flex;
    align-items: center;
    flex-direction: column; /* needed for safari centering */
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 12px 24px 12px 24px;
  `,
  DividerWrapper: css``,
  HorizontalDividerWrapper: css`
    padding-top: 16px;
    padding-bottom: 16px;
    width: 2px;
  `,
  VerticalDividerWrapper: css`
    padding-left: 51px;
    padding-right: 16px;
    height: 2px;
    width: 100%;
  `,
  HiddenDividerWrapper: css`
    display: none;
    ${MediaQueries.Medium} {
      display: block;
    }
  `,
  Divider: css`
    width: 100%;
    height: 100%;
    background-color: ${Colors.opacity(Colors.IndigoDark, 0.05)};
  `,
};



// WEBPACK FOOTER //
// ./src/ui/components/EstimateForm.js